import React, { useMemo, useState } from 'react';
import { ApiRequest } from '@/utils/axios';
import { notify } from '@/utils/notify';
import { modalStore } from '@/stores/modal.store';
import { coinflipStore } from '@/stores/coinflip.store';
import SuccessIcon from '@/components/Notifications/SuccessIcon';
import AlertIcon from '@/components/Notifications/AlertIcon';
import NotifyWrapper from '@/components/Notifications/NotifyWrapper';
import { IBaseItem } from '@/utils/types/Items';
import { ICoinflipGame } from '@/utils/types/ICoinflipGame';
import { toFullSteamAvatar } from '@/utils/avatar';
import AdminSkinPicker from '@/components/Modals/AdminAddBet/AdminSkinPicker';
import AdminBotPicker from '@/components/Modals/AdminAddBet/AdminBotPicker';

// Backend mathDifference threshold — opponents must bet within ±20% of the
// first player's value. We display the resulting acceptable range so the
// moderator picks items that won't be rejected by validateCanJoin.
const RANGE_FACTOR = 0.2;

function gameValue(game: ICoinflipGame): number {
    return (game.firstItems || []).reduce((sum, it) => sum + (it.priceUsd || 0), 0);
}

function rangeFor(firstValue: number): { min: number; max: number } {
    // |1 - first/second| <= 0.2  →  second ∈ [first / (1 + 0.2), first / (1 - 0.2)]
    return {
        min: firstValue / (1 + RANGE_FACTOR),
        max: firstValue / (1 - RANGE_FACTOR),
    };
}

const AdminJoinCoinflip = () => {
    const { toggleModal, setData } = modalStore((s) => s);
    const allGames = coinflipStore((s) => s.activeGames());

    const openGames = useMemo(
        () =>
            allGames
                .filter((g) => !g.secondUser)
                .sort((a, b) => b.id - a.id),
        [allGames]
    );

    const [selectedGameId, setSelectedGameId] = useState<number | null>(
        openGames[0]?.id ?? null
    );
    const [selected, setSelected] = useState<IBaseItem[]>([]);
    const [botUserId, setBotUserId] = useState<number | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const selectedGame = openGames.find((g) => g.id === selectedGameId);
    const totalBet = useMemo(
        () => selected.reduce((sum, it) => sum + it.priceUsd, 0),
        [selected]
    );

    const selectedRange = selectedGame
        ? rangeFor(gameValue(selectedGame))
        : null;
    const inRange =
        selectedRange &&
        totalBet >= selectedRange.min &&
        totalBet <= selectedRange.max;

    async function submit() {
        if (!selectedGame) {
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title="Error" text="Pick a coinflip game" />
            );
            return;
        }
        if (selected.length === 0) {
            notify(
                'error',
                <AlertIcon />,
                <NotifyWrapper title="Error" text="Pick at least one skin" />
            );
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await ApiRequest.post('/admin/coinflip/join', {
                itemIds: selected.map((i) => Number(i.id)),
                gameId: selectedGame.id,
                ...(botUserId ? { userId: botUserId } : {}),
            });
            if (data?.success) {
                notify(
                    'success',
                    <SuccessIcon />,
                    <NotifyWrapper
                        title="Success"
                        text={`Joined coinflip #${selectedGame.id}`}
                    />
                );
                toggleModal(false);
                setData({});
            } else {
                notify(
                    'error',
                    <AlertIcon />,
                    <NotifyWrapper
                        title="Error"
                        text={data?.response?.message || 'Failed to join'}
                    />
                );
            }
        } catch (e) {
            console.error(e);
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <div className="flex flex-col gap-4 min-w-[720px] max-md:min-w-0">
            <div className="flex flex-col gap-2">
                <div className="text-[11.5px] uppercase tracking-[0.08em] text-text_3 font-semibold">
                    Open coinflips ({openGames.length})
                </div>
                {openGames.length === 0 ? (
                    <div className="surface px-4 py-6 text-center text-text_3 text-sm">
                        No open coinflips waiting for a second player
                    </div>
                ) : (
                    <div
                        className="flex flex-col gap-2 max-h-[260px] overflow-y-auto fluent-scrollbar pr-1"
                    >
                        {openGames.map((game) => {
                            const value = gameValue(game);
                            const range = rangeFor(value);
                            const isSelected = selectedGameId === game.id;
                            return (
                                <button
                                    key={game.id}
                                    type="button"
                                    onClick={() => setSelectedGameId(game.id)}
                                    className={
                                        'flex items-center justify-between gap-3 px-3 py-2.5 rounded-fl border text-left transition-colors ' +
                                        (isSelected
                                            ? 'border-accent bg-layer_3'
                                            : 'border-stroke bg-layer_2 hover:border-stroke_strong')
                                    }
                                >
                                    <div className="flex items-center gap-2.5 min-w-0">
                                        {game.firstUser?.avatar ? (
                                            <img
                                                src={toFullSteamAvatar(game.firstUser.avatar)}
                                                alt=""
                                                className="w-8 h-8 rounded-full shrink-0"
                                            />
                                        ) : (
                                            <div className="w-8 h-8 rounded-full bg-layer_3 shrink-0" />
                                        )}
                                        <div className="flex flex-col min-w-0">
                                            <div className="text-[12.5px] font-bold text-text_main truncate">
                                                #{game.id} · {game.firstUser?.name || '—'}
                                            </div>
                                            <div className="text-[11px] text-text_3 tabular-nums">
                                                Side {game.firstSide === 1 ? 'LEFT' : 'RIGHT'} · Bet ${value.toFixed(2)}
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        className="text-[11px] font-semibold tabular-nums shrink-0"
                                        style={{
                                            color: isSelected ? '#6ee7b7' : 'var(--muted, #999)',
                                        }}
                                    >
                                        Range ${range.min.toFixed(2)} – ${range.max.toFixed(2)}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                )}
            </div>

            <AdminBotPicker value={botUserId} onChange={setBotUserId} label="Bot (joins coinflip)" />
            <AdminSkinPicker selected={selected} setSelected={setSelected} />

            <div className="flex items-center justify-between gap-3 pt-2 border-t border-stroke">
                <div className="text-[12px] text-text_3">
                    Selected:{' '}
                    <b className="text-text_main tabular-nums">{selected.length}</b>
                    <span className="mx-2 text-text_4">|</span>
                    Value:{' '}
                    <b
                        className="tabular-nums"
                        style={{
                            color: selectedRange
                                ? inRange
                                    ? '#6ee7b7'
                                    : '#ff6b6b'
                                : '#9ad6ff',
                        }}
                    >
                        ${totalBet.toFixed(2)}
                    </b>
                    {selectedRange && (
                        <>
                            <span className="mx-2 text-text_4">|</span>
                            Game range:{' '}
                            <b className="text-text_main tabular-nums">
                                ${selectedRange.min.toFixed(2)} – ${selectedRange.max.toFixed(2)}
                            </b>
                        </>
                    )}
                </div>
                <button
                    type="button"
                    onClick={submit}
                    disabled={
                        submitting ||
                        selected.length === 0 ||
                        !selectedGame ||
                        !inRange
                    }
                    className="px-5 py-2.5 rounded-fl text-[12.5px] font-bold tracking-[0.06em] text-white disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all"
                    style={{
                        background: 'linear-gradient(180deg, #4cb6ff, #2070d6)',
                        boxShadow:
                            '0 8px 20px rgba(32,112,214,0.35), inset 0 1px 0 rgba(255,255,255,0.2)',
                    }}
                >
                    {submitting ? 'Joining…' : 'JOIN'}
                </button>
            </div>
        </div>
    );
};

export default AdminJoinCoinflip;
